<script setup>
import { Head, useForm, usePage } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { isBrandDirect as checkBrandDirect } from '@/lib/brand';
import { computed, ref } from 'vue';
import { trackEvent } from '@/lib/tracking';
import { useCopy } from '@/composables/useCopy';

const props = defineProps({
    domain: Object,
    service: String,
    branding: Object,
    designVariant: Object,
    copyPack: { type: Object, default: () => ({}) },
    spotImages: { type: Object, default: () => ({}) },
});

const { t, ts, spot } = useCopy(props);

const page = usePage();
const domainName = computed(() => props.domain?.request_host || props.domain?.name || 'leadportal.scienceworks.com');
const contactEmail = computed(() => props.domain?.settings?.contact_email || (domainName.value.includes('scienceworks.com') ? 'info@scienceworks.com' : `info@${domainName.value}`));
const serviceLabel = computed(() => props.service === 'wp' ? 'Wärmepumpen' : 'Photovoltaik');
const isBrandDirect = computed(() => checkBrandDirect(props.domain));
const brandName = computed(() => props.domain?.settings?.brand_name || domainName.value);

const form = useForm({
    name: '',
    email: '',
    subject: '',
    message: '',
    service_type: props.service || 'pv',
    website: '',
});

const submitted = computed(() => page.props.flash?.contact_status === 'success');
const submitting = ref(false);

const submit = () => {
    if (!form.name || !form.email || !form.message) return;
    trackEvent('contact_form_submit', {
        event_category: 'engagement',
        service_type: props.service,
        metadata: { subject: form.subject },
    });
    form.post('/kontakt', {
        preserveScroll: true,
        onStart: () => { submitting.value = true; },
        onFinish: () => { submitting.value = false; },
        onSuccess: () => { form.reset('name', 'email', 'subject', 'message'); },
    });
};
</script>

<template>
    <Head :title="`Kontakt - ${serviceLabel}-Beratung`">
        <meta name="description" :content="`Kontaktiere uns für eine kostenlose Beratung zu ${serviceLabel}. Unser Team hilft dir gerne.`" />
        <meta property="og:type" content="website" />
    </Head>

    <AppLayout :domain="domain" :branding="branding" :designVariant="designVariant" :service="service" activePage="contact" heroMode>
        <!-- Hero -->
        <section class="sv-hero" aria-label="Kontakt">
            <div class="sv-hero__bg" aria-hidden="true"></div>
            <div class="sv-hero__content">
                <span class="sv-hero__tag">{{ t('contact_hero_tag', 'Kontakt') }}</span>
                <h1 class="sv-hero__title">{{ t('contact_hero_title_pre', 'Fragen?') }} <span class="sv-hero__accent">{{ t('contact_hero_title_accent', 'Schreib uns.') }}</span></h1>
                <p class="sv-hero__sub">{{ ts('contact_hero_sub', `Unser Team aus ${serviceLabel.value}-Experten hilft dir gerne weiter.`) }}</p>
            </div>
        </section>

        <!-- Contact -->
        <section class="sv-contact" aria-label="Kontaktformular">
            <div class="sv-contact__inner">
                <!-- Details -->
                <div class="sv-contact__details">
                    <div class="sv-contact__trust-strip">
                        <span class="sv-contact__trust-item">{{ t('contact_trust_1', 'Seriös & geprüft') }}</span>
                        <span class="sv-contact__trust-sep">|</span>
                        <span class="sv-contact__trust-item">{{ t('contact_trust_2', 'Antwort in 24h') }}</span>
                        <span class="sv-contact__trust-sep">|</span>
                        <span class="sv-contact__trust-item">{{ t('contact_trust_3', 'Kostenlos') }}</span>
                    </div>

                    <div class="sv-detail-card">
                        <div class="sv-detail-card__label">{{ t('contact_email_label', 'E-Mail') }}</div>
                        <a :href="`mailto:${contactEmail}`" class="sv-detail-card__link">{{ contactEmail }}</a>
                        <p class="sv-detail-card__hint">{{ t('contact_email_hint', 'Wir antworten in der Regel innerhalb von 24 Stunden.') }}</p>
                    </div>

                    <div class="sv-detail-card">
                        <div class="sv-detail-card__label">{{ t('contact_hours_label', 'Erreichbarkeit') }}</div>
                        <p class="sv-detail-card__text">{{ t('contact_hours_value', 'Mo - Fr: 9:00 - 17:00 Uhr') }}</p>
                        <p class="sv-detail-card__hint">{{ t('contact_hours_hint', 'Anfragen über das Formular werden auch außerhalb der Geschäftszeiten bearbeitet.') }}</p>
                    </div>

                    <div class="sv-detail-card sv-detail-card--accent">
                        <div class="sv-detail-card__label">{{ t('contact_about_label', 'Über uns') }}</div>
                        <p class="sv-detail-card__text">{{ ts('contact_about_text', `Wir sind ein Vergleichsportal und verbinden dich mit regionalen Fachbetrieben für ${serviceLabel}. Für dich kostenlos und unverbindlich. Wir finanzieren uns über eine Vermittlungsvergütung.`) }}</p>
                    </div>
                </div>

                <!-- Form -->
                <div class="sv-form-card">
                    <div v-if="submitted" class="sv-form-card__success">
                        <div class="sv-form-card__success-badge">{{ t('contact_success_badge', '[OK]') }}</div>
                        <h2 class="sv-form-card__success-title">{{ t('contact_success_title', 'Vielen Dank, deine Nachricht ist angekommen.') }}</h2>
                        <p class="sv-form-card__success-text">{{ t('contact_success_text_pre', 'Wir melden uns innerhalb von 24 Stunden bei dir. Direkt erreichbar unter') }} <a :href="`mailto:${contactEmail}`" class="sv-form-card__success-link">{{ contactEmail }}</a>.</p>
                    </div>
                    <form v-else @submit.prevent="submit" class="sv-form" novalidate>
                        <h2 class="sv-form__title">{{ t('contact_form_title', 'Nachricht senden') }}</h2>
                        <p class="sv-form__intro">{{ t('contact_form_intro', 'Alle mit * markierten Felder sind Pflichtfelder.') }}</p>
                        <div class="sv-form__row">
                            <div class="sv-form__field">
                                <label for="sv-name" class="sv-form__label">{{ t('contact_form_name_label', 'Dein Name *') }}</label>
                                <input id="sv-name" v-model="form.name" type="text" :placeholder="t('contact_form_name_placeholder', 'Max Mustermann')" class="sv-form__input" required autocomplete="name" />
                                <span v-if="form.errors?.name" class="sv-form__error">{{ form.errors.name }}</span>
                            </div>
                            <div class="sv-form__field">
                                <label for="sv-email" class="sv-form__label">{{ t('contact_form_email_label', 'Deine E-Mail *') }}</label>
                                <input id="sv-email" v-model="form.email" type="email" :placeholder="t('contact_form_email_placeholder', 'max@beispiel.de')" class="sv-form__input" required autocomplete="email" />
                                <span v-if="form.errors?.email" class="sv-form__error">{{ form.errors.email }}</span>
                            </div>
                        </div>
                        <div class="sv-form__field">
                            <label for="sv-subject" class="sv-form__label">{{ t('contact_form_subject_label', 'Betreff') }}</label>
                            <input id="sv-subject" v-model="form.subject" type="text" :placeholder="t('contact_form_subject_placeholder', 'Worum geht es?')" class="sv-form__input" />
                        </div>
                        <div class="sv-form__field">
                            <label for="sv-message" class="sv-form__label">{{ t('contact_form_message_label', 'Deine Nachricht *') }}</label>
                            <textarea id="sv-message" v-model="form.message" rows="5" :placeholder="t('contact_form_message_placeholder', 'Beschreibe dein Anliegen...')" class="sv-form__textarea" required></textarea>
                            <span v-if="form.errors?.message" class="sv-form__error">{{ form.errors.message }}</span>
                        </div>
                        <!-- Honeypot -->
                        <div aria-hidden="true" style="position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden;">
                            <label>{{ t('contact_form_honeypot_label', 'Website (bitte leer lassen)') }}</label>
                            <input type="text" v-model="form.website" tabindex="-1" autocomplete="off" />
                        </div>
                        <button type="submit" :disabled="submitting" class="sv-form__submit">
                            {{ submitting ? t('contact_form_submit_loading', 'Wird gesendet...') : t('contact_form_submit', 'Nachricht absenden') }}
                        </button>
                        <p class="sv-form__privacy">{{ t('contact_form_privacy', 'Deine Daten werden vertraulich behandelt und nicht an Dritte weitergegeben.') }}</p>
                    </form>
                </div>
            </div>
        </section>
    </AppLayout>
</template>

<style scoped>
.sv-hero { position: relative; min-height: 380px; display: flex; align-items: center; overflow: hidden; background: #0a1628; }
.sv-hero__bg { position: absolute; inset: 0; background: linear-gradient(135deg, #0a1628 0%, #001d75 100%); opacity: 0.95; }
.sv-hero__content { position: relative; z-index: 2; max-width: 720px; padding: 100px 24px 60px; }
@media (min-width: 768px) { .sv-hero__content { padding: 120px 48px 70px; margin-left: max(calc((100% - 1200px) / 2), 48px); } }
.sv-hero__tag { display: inline-block; padding: 6px 18px; border-radius: 20px; font-family: 'Inter', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #00d782; border: 1px solid rgba(0,215,130,0.3); background: rgba(0,215,130,0.08); margin-bottom: 20px; }
.sv-hero__title { font-family: 'Inter', sans-serif; font-size: clamp(32px, 5vw, 48px); font-weight: 800; color: #fff; line-height: 1.1; margin-bottom: 16px; }
.sv-hero__accent { color: #00d782; }
.sv-hero__sub { font-family: 'Inter', sans-serif; font-size: 17px; line-height: 1.7; color: rgba(255,255,255,0.7); }

.sv-contact { padding: 80px 0; background: #f0f4f8; }
.sv-contact__inner { max-width: 1100px; margin: 0 auto; padding: 0 24px; display: grid; gap: 32px; grid-template-columns: 1fr; }
@media (min-width: 1024px) { .sv-contact__inner { grid-template-columns: 1fr 1fr; gap: 48px; } }
.sv-contact__details { display: flex; flex-direction: column; gap: 20px; }
.sv-contact__trust-strip { display: flex; align-items: center; gap: 10px; padding: 12px 16px; background: rgba(0,215,130,0.06); border: 1px solid rgba(0,215,130,0.15); border-radius: 12px; flex-wrap: wrap; }
.sv-contact__trust-item { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #001d75; }
.sv-contact__trust-sep { color: #d1d5db; font-size: 12px; }
@media (max-width: 639px) { .sv-contact__trust-sep { display: none; } .sv-contact__trust-strip { justify-content: center; } }
.sv-detail-card { padding: 24px; background: #ffffff; border-radius: 16px; box-shadow: 0 2px 12px rgba(0,0,0,0.04); }
.sv-detail-card--accent { border-left: 3px solid #00d782; background: rgba(0,215,130,0.04); }
.sv-detail-card__label { font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; color: #1a1a1a; margin-bottom: 4px; }
.sv-detail-card__link { font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 600; color: #001d75; text-decoration: none; }
.sv-detail-card__link:hover { text-decoration: underline; }
.sv-detail-card__hint { font-family: 'Inter', sans-serif; font-size: 13px; color: #94a3b8; margin-top: 4px; }
.sv-detail-card__text { font-family: 'Inter', sans-serif; font-size: 14px; color: #64748b; line-height: 1.6; }

.sv-form-card { padding: 28px; background: #ffffff; border-radius: 16px; box-shadow: 0 4px 24px rgba(0,0,0,0.06); }
@media (min-width: 640px) { .sv-form-card { padding: 36px; } }
.sv-form-card__success { text-align: center; padding: 40px 0; }
.sv-form-card__success-badge { width: 64px; height: 64px; margin: 0 auto 16px; display: flex; align-items: center; justify-content: center; border-radius: 50%; background: rgba(0,215,130,0.1); color: #00d782; font-family: 'Inter', sans-serif; font-size: 16px; font-weight: 800; }
.sv-form-card__success-title { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a1a; margin-bottom: 8px; }
.sv-form-card__success-text { font-family: 'Inter', sans-serif; font-size: 15px; color: #64748b; }
.sv-form-card__success-link { color: #001d75; font-weight: 600; }
.sv-form { display: flex; flex-direction: column; gap: 20px; }
.sv-form__title { font-family: 'Inter', sans-serif; font-size: 20px; font-weight: 700; color: #1a1a1a; padding-bottom: 14px; border-bottom: 2px solid rgba(0,215,130,0.2); }
.sv-form__intro { font-family: 'Inter', sans-serif; font-size: 14px; color: #94a3b8; margin-top: -8px; }
.sv-form__row { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (min-width: 640px) { .sv-form__row { grid-template-columns: 1fr 1fr; } }
.sv-form__field { display: flex; flex-direction: column; }
.sv-form__label { font-family: 'Inter', sans-serif; font-size: 13px; font-weight: 600; color: #1a1a1a; margin-bottom: 6px; }
.sv-form__input { width: 100%; min-height: 48px; padding: 12px 16px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; color: #1a1a1a; outline: none; transition: border-color 200ms, box-shadow 200ms; }
.sv-form__input:focus { border-color: #00d782; box-shadow: 0 0 0 3px rgba(0,215,130,0.15); }
.sv-form__input::placeholder { color: #94a3b8; }
.sv-form__textarea { width: 100%; min-height: 120px; padding: 12px 16px; background: #f8fafc; border: 2px solid #e2e8f0; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; color: #1a1a1a; outline: none; resize: vertical; transition: border-color 200ms, box-shadow 200ms; }
.sv-form__textarea:focus { border-color: #00d782; box-shadow: 0 0 0 3px rgba(0,215,130,0.15); }
.sv-form__textarea::placeholder { color: #94a3b8; }
.sv-form__error { font-family: 'Inter', sans-serif; font-size: 13px; color: #ef4444; margin-top: 4px; }
.sv-form__submit { display: flex; align-items: center; justify-content: center; width: 100%; min-height: 52px; padding: 14px 24px; background: #00d782; color: #1a1a1a; border: none; border-radius: 12px; font-family: 'Inter', sans-serif; font-size: 15px; font-weight: 700; cursor: pointer; transition: transform 200ms, box-shadow 200ms; }
.sv-form__submit:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,215,130,0.35); }
.sv-form__submit:disabled { opacity: 0.6; cursor: not-allowed; }
.sv-form__privacy { font-family: 'Inter', sans-serif; font-size: 12px; color: #94a3b8; text-align: center; }
</style>
